
import { supabase } from "@/integrations/supabase/client";
import { Article } from "@/types/news";

export const getUserBookmarks = async (userId: string): Promise<Article[]> => {
  try {
    const { data, error } = await supabase
      .from('bookmarks')
      .select(`
        id,
        created_at,
        articles (
          id,
          title,
          excerpt,
          content,
          url,
          image_url,
          published_at,
          source_id,
          source_name,
          source_url,
          source_logo_url,
          author_name,
          author_avatar,
          category,
          read_time,
          likes_count,
          comments_count
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data
      ?.filter((bookmark): bookmark is typeof bookmark & { articles: NonNullable<typeof bookmark.articles> } => bookmark.articles !== null)
      .map(bookmark => ({
      id: bookmark.articles.id,
      title: bookmark.articles.title,
      excerpt: bookmark.articles.excerpt,
      content: bookmark.articles.content ?? undefined,
      url: bookmark.articles.url,
      imageUrl: bookmark.articles.image_url ?? '',
      publishedAt: bookmark.articles.published_at,
      source: {
        id: bookmark.articles.source_id,
        name: bookmark.articles.source_name,
        url: bookmark.articles.source_url ?? undefined,
        logoUrl: bookmark.articles.source_logo_url ?? undefined
      },
      author: bookmark.articles.author_name ? {
        name: bookmark.articles.author_name,
        avatar: bookmark.articles.author_avatar ?? undefined
      } : undefined,
      category: bookmark.articles.category ?? undefined,
      readTime: bookmark.articles.read_time ?? undefined,
      isBookmarked: true
    })) || [];
  } catch (error) {
    console.error('Error fetching user bookmarks:', error);
    throw error;
  }
};

export const addBookmark = async (userId: string, articleId: string): Promise<void> => {
  try {
    // Ensure user profile exists
    await ensureUserProfile(userId);

    const { error } = await supabase
      .from('bookmarks')
      .insert({
        user_id: userId,
        article_id: articleId
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error adding bookmark:', error);
    throw error;
  }
};

export const removeBookmark = async (userId: string, articleId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('bookmarks')
      .delete()
      .eq('user_id', userId)
      .eq('article_id', articleId);

    if (error) throw error;
  } catch (error) {
    console.error('Error removing bookmark:', error);
    throw error;
  }
};

export const isArticleBookmarked = async (userId: string, articleId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('bookmarks')
      .select('id')
      .eq('user_id', userId)
      .eq('article_id', articleId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;

    return !!data;
  } catch (error) {
    console.error('Error checking bookmark status:', error);
    return false;
  }
};

// Helper function to ensure user profile exists
const ensureUserProfile = async (userId: string) => {
  const { data: existingProfile } = await supabase
    .from('user_profiles')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (!existingProfile) {
    await supabase
      .from('user_profiles')
      .insert({
        id: userId,
        username: `user_${userId.substring(0, 8)}`,
        full_name: 'User',
        total_xp: 0
      });
  }
};
